import Image from 'next/image';

import { joinSgcmMedia } from '@/utils/url/join/public';

import { ICON_SIZE_IN_PIXEL } from './constants';
import styles from './styles.module.scss';
import type { ConvertFunction } from './types';

export const convertValueToText = (value: string | number | boolean | null) => {
  if (value === null || value === '0' || value === '') return 'unknown';

  if (value === true) {
    return (
      <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />
    );
  }

  if (value === false || value === '2' || value === 'No') {
    return (
      <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />
    );
  }
  return `${value}`;
};

export const checkIsNullOrFalseOrZero = (
  value: number | boolean | null | string,
): boolean => value === null
|| value === false
|| value === 0
|| value === '0'
|| value === '';

export const conversionMap: Record<string, ConvertFunction> = {
  driverElectricSeats: (value: string | number | null) => {
    const map: Record<number, JSX.Element | string> = {
      0: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      1: <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      2:
  <>
    <Image
      alt="icon bold check"
      className={styles.iconBoldCheck}
      height={ICON_SIZE_IN_PIXEL}
      src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
      width={ICON_SIZE_IN_PIXEL} />
    {' '}
    w/ memory
  </>,
    };
    return value !== null ? map[value as number] : 'unknown';
  },
  ventilatedSeats: (value: string | number | null) => {
    const map: Record<string, string | JSX.Element> = {
      1: 'Front',
      2: 'Front & Rear',
      3: 'Driver',
      4: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
    };
    return !(value === null || value === '0') ? map[value] : 'unknown';
  },
  electricSlidingDoor: (value: string | number | null) => {
    const map: Record<string, string | JSX.Element> = {
      1: 'Single',
      2: 'Dual',
      3: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
    };
    return !(value === null || value === '0') ? map[value] : 'unknown';
  },
  carPlay: (value: string | number | null) => {
    const map: Record<string, string | JSX.Element> = {
      1: 'Wired',
      2: 'Wireless',
      3: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
    };
    return !(value === null || value === '0') ? map[value] : 'unknown';
  },
  electricParkBrake: (value: string | number | null) => {
    const map: Record<number, JSX.Element | string> = {
      0: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      1: <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      2:
  <>
    <Image
      alt="icon bold check"
      className={styles.iconBoldCheck}
      height={ICON_SIZE_IN_PIXEL}
      src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
      width={ICON_SIZE_IN_PIXEL} />
    {' '}
    w/Auto-hold
  </>,
    };
    return value !== null ? map[value as number] : 'unknown';
  },
  parkingSensors: (value: string | number | null) => {
    const map: Record<number, JSX.Element | string> = {
      0: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      1: 'Rear',
      2: 'Front & Rear',
      3: 'Front, Side & Rear',
    };
    return !(value === null || value === '0') ? map[value as number] : 'unknown'; // @suppress longLineCheck
  },
  reverseCamera: (value: string | number | null) => {
    const map: Record<number, JSX.Element | string> = {
      0: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      1: <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      2:
  <>
    <Image
      alt="icon bold check"
      className={styles.iconBoldCheck}
      height={ICON_SIZE_IN_PIXEL}
      src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
      width={ICON_SIZE_IN_PIXEL} />
    <span>w/ Dynamic Guidelines</span>
  </>,
    };
    return value !== null ? map[value as number] : 'unknown';
  },
  cruiseControl: (value: string | number | null) => {
    const map: Record<number, JSX.Element | string> = {
      0: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      1: <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      2: 'Adaptive',
    };
    return value !== null ? map[value as number] : 'unknown';
  },
  blindSpotMonitor: (value: string | number | null) => {
    const map: Record<string, JSX.Element | string> = {
      1: <Image
        alt="icon bold check"
        className={styles.iconBoldCheck}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      2: <Image
        alt="icon bold cross"
        className={styles.iconBoldCross}
        height={ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCross.svg')}`}
        width={ICON_SIZE_IN_PIXEL} />,
      3:
  <>
    <Image
      alt="icon bold check"
      className={styles.iconBoldCheck}
      height={ICON_SIZE_IN_PIXEL}
      src={`${joinSgcmMedia('/public/new-cars/icon/iconBoldCheck.svg')}`}
      width={ICON_SIZE_IN_PIXEL} />
    w/ Display
  </>,
    };
    return !(value === null || value === '0') ? map[value] : 'unknown';
  },
};

export const convertAirbags = (value: number): string => {
  if (value === -1) {
    return 'unknown';
  }

  if (value === 0) {
    return 'No Airbag';
  }
  return `${value}`;
};

export const getConvertedValue = (key: string, value: string | number | null): string | JSX.Element => ( // @suppress longLineCheck
  conversionMap[key] ? conversionMap[key](value) : convertValueToText(value)
);
